import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const FreelancersVsEarningsPage = () => {
  const [earningsData, setEarningsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'freelancer_id', direction: 'ascending' });

  const apiBaseURL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchEarningsData = async () => {
      const token = localStorage.getItem('employeeToken');
      if (!token) {
        window.location.href = "/employee-login";
        return;
      }

      try {
        const response = await axios.get(
          `${apiBaseURL}/api/freelancer-earning-data`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log("📥 Raw earnings data received from backend:", response.data);
        setEarningsData(response.data);
      } catch (error) {
        console.error("❌ Error fetching earnings data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEarningsData();
  }, [apiBaseURL]);

  // Group and summarize
  const groupedData = earningsData.reduce((acc, {
    freelancer_id,
    freelancer_name,
    order_id,
    payout_eligible_amount,
    subscription_plan_names
  }) => {
    if (!acc[freelancer_id]) {
      acc[freelancer_id] = {
        freelancer_id,
        freelancer_name,
        order_ids: [],
        total_payout_eligible_amount: 0,
        subscription_plan_names: subscription_plan_names || ''
      };
    }
    if (order_id !== null) {
      acc[freelancer_id].order_ids.push(order_id);
      acc[freelancer_id].total_payout_eligible_amount += Number(payout_eligible_amount || 0);
    }
    return acc;
  }, {});

  const groupedArray = Object.values(groupedData);

  groupedArray.sort((a, b) => {
    let aKey = a[sortConfig.key];
    let bKey = b[sortConfig.key];
    if (sortConfig.key === 'order_id') {
      aKey = a.order_ids[0] || '';
      bKey = b.order_ids[0] || '';
    }
    if (sortConfig.key === 'payout_eligible_amount') {
      aKey = a.total_payout_eligible_amount;
      bKey = b.total_payout_eligible_amount;
    }
    if (aKey < bKey) return sortConfig.direction === 'ascending' ? -1 : 1;
    if (aKey > bKey) return sortConfig.direction === 'ascending' ? 1 : -1;
    return 0;
  });

  const totalFreelancers = groupedArray.length;
  const totalOrders = earningsData.filter(e => e.order_id !== null).length;
  const totalPayoutEligibleAmount = groupedArray.reduce((sum, item) => sum + item.total_payout_eligible_amount, 0);

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleDownloadExcel = () => {
    const wsData = [
      ["OrderMinds Freelancing Solutions LLP"],
      ["Subscriptions And Payouts"],
      [`Downloaded on: ${new Date().toLocaleString()}`],
      [],
      ["Freelancer ID", "Freelancer Name", "Subscription Plans", "Order IDs (Total orders completed)", "Total Payout Eligible Amount"],
      ...groupedArray.map(item => [
        item.freelancer_id,
        item.freelancer_name,
        item.subscription_plan_names || 'N/A',
        item.order_ids.length > 0 ? item.order_ids.join(', ') : 'No orders',
        item.total_payout_eligible_amount.toFixed(2)
      ]),
      ["TOTAL", "", "", "", totalPayoutEligibleAmount.toFixed(2)]
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Earnings");

    const now = new Date();
    const timestamp = now.toISOString().replace(/[:.]/g, '-');
    const filename = `Subscriptions_And_Payouts_${timestamp}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  if (loading) return <div>Loading earnings data...</div>;

  return (
    <div className="p-4" style={{ marginLeft: '18rem' }}>
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-bold">Freelancer Earnings Summary</h1>
        <button
          onClick={handleDownloadExcel}
          className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded"
        >
          Download Excel
        </button>
      </div>

      <div className="mb-4 space-x-8 text-gray-700 font-semibold">
        <span>Total Freelancers: {totalFreelancers}</span>
        <span>Total Orders: {totalOrders}</span>
        <span>Total Payout Eligible Amount: ₹{totalPayoutEligibleAmount.toFixed(2)}</span>
      </div>

      {groupedArray.length === 0 ? (
        <p className="text-gray-500">No earnings data found</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="table-auto border border-gray-400 w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="px-4 py-2 cursor-pointer border border-gray-400" onClick={() => handleSort('freelancer_id')}>
                  Freelancer ID {sortConfig.key === 'freelancer_id' ? (sortConfig.direction === 'ascending' ? '🔼' : '🔽') : ''}
                </th>
                <th className="px-4 py-2 border border-gray-400">Freelancer Name</th>
                <th className="px-4 py-2 border border-gray-400">Subscription Plans</th>
                <th className="px-4 py-2 cursor-pointer border border-gray-400" onClick={() => handleSort('order_id')}>
                  Order IDs (Total orders completed) {sortConfig.key === 'order_id' ? (sortConfig.direction === 'ascending' ? '🔼' : '🔽') : ''}
                </th>
                <th className="px-4 py-2 cursor-pointer border border-gray-400" onClick={() => handleSort('payout_eligible_amount')}>
                  Total Payout Eligible Amount {sortConfig.key === 'payout_eligible_amount' ? (sortConfig.direction === 'ascending' ? '🔼' : '🔽') : ''}
                </th>
              </tr>
            </thead>
            <tbody>
              {groupedArray.map(({ freelancer_id, freelancer_name, subscription_plan_names, order_ids, total_payout_eligible_amount }) => (
                <tr key={freelancer_id} className="border-t border-gray-300">
                  <td className="px-4 py-2 border border-gray-400">{freelancer_id}</td>
                  <td className="px-4 py-2 border border-gray-400">{freelancer_name}</td>
                  <td className="px-4 py-2 border border-gray-400">{subscription_plan_names || 'N/A'}</td>
                  <td className="px-4 py-2 border border-gray-400">
                    {order_ids.length > 0 ? order_ids.join(', ') : 'No orders'}
                  </td>
                  <td className="px-4 py-2 border border-gray-400">{total_payout_eligible_amount.toFixed(2)}</td>
                </tr>
              ))}
              <tr className="font-bold bg-gray-50 border-t border-gray-400">
                <td className="px-4 py-2 border border-gray-400">TOTAL</td>
                <td className="px-4 py-2 border border-gray-400"></td>
                <td className="px-4 py-2 border border-gray-400"></td>
                <td className="px-4 py-2 border border-gray-400"></td>
                <td className="px-4 py-2 border border-gray-400">{totalPayoutEligibleAmount.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FreelancersVsEarningsPage;
